import { useRef, useState, useEffect } from "react";
import { useTodos, useAuthState, toggleTodo, type Todo } from "../state";

export function TodoListElement({
  todo,
  onClick,
}: {
  todo: Todo;
  onClick: () => void;
}) {
  return (
    <li
      key={todo.id}
      className="grid grid-cols-[auto_1fr] items-start gap-2 cursor-pointer"
      onClick={() => onClick()}
    >
      <span className="w-5 h-5 mt-2 rounded border-2 border-black/50 flex items-center justify-center flex-shrink-0">
        {todo.done && (
          <svg
            className="w-5 h-5 text-black/70"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span
        className={`${
          todo.done ? "text-black/50 line-through" : "text-black/70"
        }`}
      >
        {todo.text}
      </span>
    </li>
  );
}

function useOverflowCount(
  containerRef: React.RefObject<HTMLElement | null>,
  itemCount: number,
) {
  const [hiddenCount, setHiddenCount] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkOverflow = () => {
      const items = container.querySelectorAll("li");
      let hidden = 0;
      const containerRect = container.getBoundingClientRect();

      items.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        if (itemRect.bottom > containerRect.bottom) {
          hidden++;
        }
      });

      setHiddenCount(hidden);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [containerRef, itemCount]);

  return hiddenCount;
}

export function TodoList() {
  const todos = useTodos();
  const { headers } = useAuthState();
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenCount = useOverflowCount(containerRef, todos.length);

  const handleToggle = (id: number) => {
    toggleTodo(id, headers);
  };

  return (
    <div className="h-full flex flex-col py-4">
      <h2 className="mb-3 text-black/70 font-bold">Aufgaben</h2>
      {todos.length === 0 ? (
        <p className="text-black/40 text-base">Keine Aufgaben</p>
      ) : (
        <div className="relative flex-1 min-h-0">
          <div
            ref={containerRef}
            className="h-full flex gap-1 md:overflow-y-hidden"
          >
            <ul className={`flex-col flex-1 flex md:hidden gap-0.5`}>
              {todos.map((todo) => (
                <TodoListElement
                  key={todo.id}
                  todo={todo}
                  onClick={() => handleToggle(todo.id)}
                />
              ))}
            </ul>
            <ul className={`flex-col flex-1 hidden md:flex gap-0.5`}>
              {todos
                .filter((_, key) => key % 2 === 0)
                .map((todo) => (
                  <TodoListElement
                    key={todo.id}
                    todo={todo}
                    onClick={() => handleToggle(todo.id)}
                  />
                ))}
            </ul>
            <ul className={`flex-col flex-1 hidden md:flex gap-0.5`}>
              {todos
                .filter((_, key) => key % 2 === 1)
                .map((todo) => (
                  <TodoListElement
                    key={todo.id}
                    todo={todo}
                    onClick={() => handleToggle(todo.id)}
                  />
                ))}
            </ul>
          </div>
          {hiddenCount > 0 && (
            <div className="absolute bottom-0 left-0 right-0 text-left bg-gray-400">
              <span className="text-black/50 text-base font-medium">
                {hiddenCount} weitere...
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
